import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  TenantState,
  TenantContext,
  TenantInitializationStrategy,
  TenancyMode,
} from 'src/types/tenant';
import { getRuntimeConfig } from 'src/utils/runtimeConfig';
import { canCreateTenant } from 'src/utils/tenancyMode';
import getUserSimple, {
  type UserContext,
} from 'src/services/user/getUserSimple';
import getTenantsSimple from 'src/services/tenants/getTenantsSimple';
import saveUserTenant from 'src/services/user/saveUserTenant';
import setUserTenant from 'src/services/user/setUserTenant';
import { isAuthorizationFailure } from 'src/utils/errors/isAuthorizationFailure';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getErrorMessage(error: unknown, fallback: string): string {
  return isRecord(error) && typeof error.message === 'string'
    ? error.message
    : fallback;
}

function hasErrorStatus(error: unknown, statusCode: number): boolean {
  return (
    isRecord(error) &&
    (error.status === statusCode || error.statusCode === statusCode)
  );
}

export const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      currentTenant: null,
      currentUser: null,
      isInitialized: false,
      isLoading: false,
      availableTenants: [],
      tenancyMode: 'multi', // Will be initialized lazily
      // New properties for automatic tenant resolution
      tenantId: null,
      loading: false,
      error: null,
      authorizationError: false,
      actions: {
        setCurrentTenant: (tenant: TenantContext) => {
          set({ currentTenant: tenant });
        },

        updateCurrentTenantConfiguration: (configuration) => {
          set((state) => {
            if (!state.currentTenant) return state;

            const currentTenant = {
              ...state.currentTenant,
              configuration: {
                ...state.currentTenant.configuration,
                ...configuration,
              },
            };

            return {
              currentTenant,
              availableTenants: state.availableTenants.map((tenant) =>
                tenant.id === currentTenant.id ? currentTenant : tenant,
              ),
            };
          });
        },

        setCurrentUser: (user: UserContext) => {
          set({ currentUser: user });
        },

        setAvailableTenants: (tenants: TenantContext[]) => {
          set({ availableTenants: [...tenants] });
        },

        setIsLoading: (loading: boolean) => {
          set({ isLoading: loading });
        },

        setIsInitialized: (initialized: boolean) => {
          set({ isInitialized: initialized });
        },

        initializeTenant: async () => {
          const state = get();
          if (state.isInitialized) return;

          console.log('🔍 Tenant store initialization starting:', {
            currentTenant: state.currentTenant,
            isInitialized: state.isInitialized,
            availableTenants: state.availableTenants,
            tenancyMode: state.tenancyMode,
          });

          set({ isLoading: true, authorizationError: false, error: null });

          try {
            // Initialize tenancy mode from config
            await initializeTenancyMode();

            // In single-tenant mode, try to resolve tenant automatically first
            const currentState = get();
            if (currentState.tenancyMode === 'single') {
              await currentState.actions.resolveTenantId();

              if (get().authorizationError) {
                set({ isInitialized: true });
                return;
              }

              // If tenant was resolved successfully, we're done
              if (get().tenantId) {
                set({ isInitialized: true });
                return;
              }
            }

            const strategy = getTenantInitializationStrategy();
            console.log('🔍 Using initialization strategy:', strategy);

            switch (strategy) {
              case 'user-tenant':
                await initializeFromUserTenant();
                break;
              case 'environment':
                await initializeFromEnvironment();
                break;
              case 'selection':
                await initializeFromSelection();
                break;
            }

            set({ isInitialized: true });
          } catch (error) {
            console.error('Failed to initialize tenant:', error);

            if (isAuthorizationFailure(error)) {
              set({
                authorizationError: true,
                error:
                  error.message ||
                  'You do not have permission to access this application.',
                currentTenant: null,
                currentUser: null,
                availableTenants: [],
                tenantId: null,
                isInitialized: true,
              });
              return;
            }

            // In single-tenant mode, don't fallback to environment variables
            // This prevents reverting to hardcoded "tenant_1" values
            const config = getRuntimeConfig();
            const tenancyMode = config.VITE_TENANCY_MODE ?? 'multi';

            if (tenancyMode === 'single') {
              console.log(
                '🔍 Single-tenant mode: Skipping environment fallback to prevent hardcoded tenant usage',
              );
              // Just mark as initialized without setting a tenant
              // The app will show tenant creation UI
            } else {
              // Only fallback to environment in multi-tenant mode
              await initializeFromEnvironment();
            }

            set({ isInitialized: true });
          } finally {
            set({ isLoading: false });
          }
        },

        switchTenant: async (tenantId: string) => {
          const state = get();
          const tenant = state.availableTenants.find((t) => t.id === tenantId);

          if (!tenant) {
            throw new Error(`Tenant with ID ${tenantId} not found`);
          }

          await setUserTenant(tenantId);

          try {
            await saveUserTenant({
              userId: 'myself',
              tenantId: tenantId,
            });
          } catch {
            // Intentionally empty - errors are silently ignored
          }

          set({ currentTenant: tenant });
        },

        clearTenant: () => {
          set({
            currentTenant: null,
            isInitialized: false,
            availableTenants: [],
            tenantId: null,
            loading: false,
            error: null,
            authorizationError: false,
          });
        },

        setTenancyMode: (mode: TenancyMode) => {
          set({ tenancyMode: mode });
        },

        canCreateTenant: () => {
          const state = get();
          // In single-tenant mode, exclude system tenants from the count
          if (state.tenancyMode === 'single') {
            // Use a simple check for system tenants to avoid circular imports
            const organizationTenants = state.availableTenants.filter(
              (tenant) => {
                const lowerName = tenant.name.toLowerCase();
                const lowerSchema = tenant.schema.toLowerCase();
                // Check if it's a system tenant (master, system, admin, default)
                const isSystem =
                  ['master', 'system', 'admin', 'default'].includes(
                    lowerName,
                  ) ||
                  ['master', 'system', 'admin', 'default'].some((pattern) =>
                    lowerSchema.includes(pattern),
                  );
                return !isSystem;
              },
            );
            return canCreateTenant(organizationTenants.length);
          }
          // In multi-tenant mode, count all tenants
          return canCreateTenant(state.availableTenants.length);
        },

        isSingleTenantMode: () => {
          const state = get();
          return state.tenancyMode === 'single';
        },

        isMultiTenantMode: () => {
          const state = get();
          return state.tenancyMode === 'multi';
        },

        // New actions for automatic tenant resolution
        setTenantId: (tenantId: string | null) => {
          set({ tenantId });
        },

        setLoading: (loading: boolean) => {
          set({ loading });
        },

        setError: (error: string | null) => {
          set({ error });
        },

        setAuthorizationError: (authorizationError: boolean) => {
          set({ authorizationError });
        },

        resolveTenantId: async () => {
          const state = get();
          const config = getRuntimeConfig();
          const tenantName = config.VITE_TENANT_NAME;

          // Only resolve in single-tenant mode and if tenant name is configured
          if (
            state.tenancyMode !== 'single' ||
            !tenantName ||
            typeof tenantName !== 'string'
          ) {
            return;
          }

          set({ loading: true, error: null });

          try {
            // Import the service function dynamically to avoid circular dependencies
            const { default: getTenantByName } = await import(
              'src/services/tenants/getTenantByName'
            );

            const response = await getTenantByName(tenantName);

            console.log('🔍 Tenant response:', response);

            if (response.success && response.tenant) {
              // Store the tenant ID and create a TenantContext
              const tenantContext: TenantContext = {
                id: response.tenant.id,
                name: response.tenant.name,
                schema: response.tenant.schema,
                status: response.tenant.status,
                configuration: response.tenant.configuration,
              };

              set({
                tenantId: response.tenant.id,
                currentTenant: tenantContext,
                error: null,
                authorizationError: false,
              });

              console.log('🔍 Tenant resolved successfully:', tenantContext);
            } else {
              throw new Error('Invalid response from tenant API');
            }
          } catch (error: unknown) {
            console.log('🔍 Tenant resolution failed:', error);

            if (isAuthorizationFailure(error)) {
              set({
                tenantId: null,
                currentTenant: null,
                error:
                  error.message ||
                  'You do not have permission to access this application.',
                authorizationError: true,
              });
              return;
            }

            // Handle 404 - tenant doesn't exist, clear tenantId to trigger creation form
            const errorMessage = getErrorMessage(
              error,
              'Failed to resolve tenant',
            );
            if (hasErrorStatus(error, 404) || errorMessage.includes('404')) {
              set({ tenantId: null, error: null });
              console.log('🔍 Tenant not found, will show creation form');
            } else {
              // Other errors - store error message
              set({ tenantId: null, error: errorMessage });
              console.error('🔍 Tenant resolution error:', errorMessage);
            }
          } finally {
            set({ loading: false });
          }
        },
      },
    }),
    {
      name: 'tenant-store',
      partialize: (state) => {
        return {
          currentTenant: state.currentTenant,
          availableTenants: state.availableTenants,
          tenancyMode: state.tenancyMode,
          tenantId: state.tenantId,
        };
      },
    },
  ),
);

// Helper functions
export function getTenantInitializationStrategy(): TenantInitializationStrategy {
  const config = getRuntimeConfig();

  // Check tenancy mode first - single-tenant mode always uses selection strategy
  // This ensures that VITE_TENANT_ID is ignored in single-tenant mode
  const tenancyMode = config.VITE_TENANCY_MODE ?? 'multi';
  if (tenancyMode === 'single') {
    return 'selection';
  }

  // In multi-tenant mode, if we have a hardcoded tenant ID, use environment strategy
  if (config.VITE_TENANT_ID && config.VITE_TENANT_ID !== 'replace-this') {
    return 'environment';
  }

  // Default to user-tenant strategy for multi-tenant mode
  return 'user-tenant';
}

async function initializeFromUserTenant() {
  try {
    // Get user information to extract tenant ID
    const user = await getUserSimple('myself');

    if (user.tenantId) {
      // Fetch tenant details
      const tenantsResponse = await getTenantsSimple();

      const userTenant = tenantsResponse.tenants.find(
        (t) => t.id === user.tenantId,
      );

      if (userTenant) {
        const tenantContext: TenantContext = {
          id: userTenant.id,
          name: userTenant.name,
          schema: userTenant.schema,
          status: userTenant.status,
          configuration: userTenant.configuration,
        };
        // Set all data atomically using the store's set function
        const availableTenants = [
          tenantContext,
          ...user.allowedTenants.map((t) => ({
            id: t.tenant.id,
            name: t.tenant.name,
            schema: 'tenant_schema_' + t.tenant.id,
            status: 'active',
            configuration: t.tenant.configuration,
          })),
        ];

        // Use the store's internal set function directly
        useTenantStore.setState({
          currentTenant: tenantContext,
          currentUser: user,
          availableTenants: availableTenants,
        });
        useTenantStore.persist.rehydrate(); // Force persist cycle
        return;
      }
    }

    throw new Error('No tenant found for user');
  } catch (error) {
    console.error('Failed to initialize from user tenant:', error);
    throw error;
  }
}

async function initializeFromEnvironment() {
  const config = getRuntimeConfig();
  const envTenantId =
    typeof config.VITE_TENANT_ID === 'string' ? config.VITE_TENANT_ID : '';

  if (!envTenantId || envTenantId === 'replace-this') {
    throw new Error('No tenant ID in environment');
  }

  try {
    // Fetch all tenants to find the one matching the environment variable
    const tenantsResponse = await getTenantsSimple();

    const envTenant = tenantsResponse.tenants.find((t) => t.id === envTenantId);

    if (envTenant) {
      const tenantContext: TenantContext = {
        id: envTenant.id,
        name: envTenant.name,
        schema: envTenant.schema,
        status: envTenant.status,
        configuration: envTenant.configuration,
      };

      // Use the store's internal set function directly
      useTenantStore.setState({
        currentTenant: tenantContext,
        availableTenants: [tenantContext],
      });
    } else {
      // Create a minimal tenant context from environment
      const tenantContext: TenantContext = {
        id: envTenantId,
        name: `Tenant ${envTenantId}`,
        schema: '',
        status: 'active',
        configuration: {
          magicLinkAuth: {
            enabled: false,
          },
          isSuperTenant: false,
        },
      };

      // Use the store's internal set function directly
      useTenantStore.setState({
        currentTenant: tenantContext,
        availableTenants: [tenantContext],
      });
      useTenantStore.persist.rehydrate(); // Force persist cycle
    }
  } catch (error) {
    console.error('Failed to initialize from environment:', error);
    throw error;
  }
}

async function initializeFromSelection() {
  try {
    // Fetch all available tenants for selection
    const tenantsResponse = await getTenantsSimple();

    const tenantContexts: TenantContext[] = tenantsResponse.tenants.map(
      (tenant) => ({
        id: tenant.id,
        name: tenant.name,
        schema: tenant.schema,
        status: tenant.status,
        configuration: tenant.configuration,
      }),
    );

    useTenantStore.getState().actions.setAvailableTenants(tenantContexts);
    useTenantStore.persist.rehydrate(); // Force persist cycle

    // In single-tenant mode, check if we have an organization tenant and auto-select it
    const config = getRuntimeConfig();
    const tenancyMode = config.VITE_TENANCY_MODE ?? 'multi';

    console.log('🔍 Tenant store initialization debug:', {
      tenancyMode,
      tenantContexts: tenantContexts.map((t) => ({
        name: t.name,
        schema: t.schema,
        isSystem:
          t.name.toLowerCase() === 'master' ||
          t.schema.toLowerCase().includes('master'),
      })),
      currentTenant: useTenantStore.getState().currentTenant,
    });

    if (tenancyMode === 'single') {
      // In single-tenant mode, preserve persisted organization tenant but clear system tenants
      const currentState = useTenantStore.getState();
      if (currentState.currentTenant) {
        // Import isSystemTenant to check if the persisted tenant is a system tenant
        const { isSystemTenant } = await import('src/utils/tenancyMode');

        if (isSystemTenant(currentState.currentTenant)) {
          console.log(
            '🔍 Single-tenant mode: Clearing persisted system tenant:',
            currentState.currentTenant,
          );
          // Clear system tenants but keep initialization state
          useTenantStore.setState({ currentTenant: null });
        } else {
          const refreshedTenant = tenantContexts.find(
            (tenant) => tenant.id === currentState.currentTenant?.id,
          );

          console.log(
            '🔍 Single-tenant mode: Preserving persisted organization tenant:',
            refreshedTenant ?? currentState.currentTenant,
          );
          // Keep the organization tenant selection, but refresh its server-backed
          // configuration so deploys do not leave a stale persisted snapshot.
          if (refreshedTenant) {
            useTenantStore.setState({ currentTenant: refreshedTenant });
          }
        }
      } else {
        console.log('🔍 Single-tenant mode: No persisted tenant found');
      }
    }

    // In multi-tenant mode, don't set current tenant - let user select
    // This will require a tenant selection UI component
  } catch (error) {
    console.error('Failed to initialize tenant selection:', error);
    throw error;
  }
}

async function initializeTenancyMode() {
  try {
    // Dynamically import to avoid circular dependencies and runtime config issues
    const { getTenancyMode } = await import('src/utils/tenancyMode');
    const mode = getTenancyMode();
    useTenantStore.getState().actions.setTenancyMode(mode);
  } catch (error) {
    console.error(
      'Failed to initialize tenancy mode, defaulting to multi:',
      error,
    );
    // Default to multi-tenant mode if there's an error
    useTenantStore.getState().actions.setTenancyMode('multi');
  }
}

// Utility function to get current tenant ID for API calls
export const getCurrentTenantId = (): string | null => {
  const state = useTenantStore.getState();
  return state.currentTenant?.id ?? null;
};

export const getCurrentTenant = (): TenantContext | null => {
  const state = useTenantStore.getState();
  return state.currentTenant ?? null;
};

export const getCurrentUser = (): UserContext | null => {
  const state = useTenantStore.getState();
  return state.currentUser ?? null;
};
